import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const areas = [
  { id: 'all', label: 'All areas' },
  { id: 'grc', label: 'Governance & Maturity' },
  { id: 'ops', label: 'IT Operations & SLA' },
  { id: 'creative', label: 'Creative & Engineering' },
];

const cases = [
  {
    id: 'maturity-telecom',
    index: '01',
    label: 'Cybersecurity maturity',
    area: 'Governance & Maturity',
    areaRefs: ['grc'],
    tags: ['ISO 27001', 'NIST CSF', 'Telco Regs'],
    title: 'Cybersecurity maturity assessment for a regional telecommunications provider',
    context:
      'The organization needed a clear baseline of its current cybersecurity posture against national regulations and international standards to guide a multi-year capability improvement program.',
    contribution:
      'Led the assessment across core domains: governance, identity, network architecture, and incident management. Mapped current-state evidence to framework requirements.',
    approach:
      'Structured the assessment around practical maturity levels, pairing policy review with technical controls verification and stakeholder interviews to avoid paper-only compliance.',
    output:
      'Maturity scorecard with domain-level gap analysis, prioritized remediation roadmap, and executive summary for the board risk committee.',
  },
  {
    id: 'sla-incident',
    index: '02',
    label: 'IT operations review',
    area: 'IT Operations & SLA',
    areaRefs: ['ops'],
    tags: ['ISO 20000', 'ITSM', 'SLA Analytics', 'Incident Management'],
    title: 'IT operations and incident management review for a financial services enterprise',
    context:
      'An enterprise operating critical financial transaction infrastructure required an independent review of its IT service management processes following an incident.',
    contribution:
      'Reviewed incident management workflows, SLA definitions, escalation paths, and post-incident review practices against ISO 20000 and internal operating standards.',
    approach:
      'Analyzed three months of incident logs, evaluated ticketing system configurations, and interviewed frontline operations personnel to identify where process departed from documentation.',
    output:
      'Operational gap report identifying systemic bottleneck in tier-2 escalation, revised SLA calculation methodology, and recommendations for automated incident tagging.',
  },
  {
    id: 'framework-alignment',
    index: '03',
    label: 'Controls testing',
    area: 'Governance & Maturity',
    areaRefs: ['grc'],
    tags: ['ISO 27001', 'COBIT 2019', 'Controls Testing', 'Multi-Entity'],
    title: 'Multi-framework controls testing program for a diversified holding company',
    context:
      'A conglomerate with subsidiaries across manufacturing, retail, and digital services needed a unified controls testing approach across disparate IT environments.',
    contribution:
      'Designed and executed controls testing work programs covering access management, change management, backup integrity, and vendor risk across five subsidiary operating units.',
    approach:
      'Developed a standardized evidence request list and testing template mapped simultaneously to ISO 27001, COBIT 2019, and local regulatory requirements, minimizing duplicate requests to operational teams.',
    output:
      'Consolidated controls testing matrix, subsidiary-specific finding registers with risk ratings, and a shared remediation tracker used across business units.',
  },
  {
    id: 'sensamakara',
    index: '04',
    label: 'Interactive system & game design',
    area: 'Creative & Engineering',
    areaRefs: ['creative'],
    tags: ['OpenCV', 'Assistive Tech', 'Fuzzy Suggestion', 'GEMASTIK XV'],
    title: 'Sensamakara: Serious game and accessible communication interface',
    context:
      'Universitas Indonesia team project for GEMASTIK XV (Bronze Medalist) and selected for exhibition at the Japan Design, Idea and Invention Expo (JDIE) 2023.',
    contribution:
      'Co-developed the interaction architecture, game mechanics, and input processing pipeline combining camera blink-detection with predictive communication.',
    approach:
      'Treated accessibility not as a bolt-on feature but as the core constraint: every interaction had to work reliably through a single binary input channel (eye blink) with minimal latency.',
    output:
      'Functional assistive game prototype, technical documentation, and presentation materials for national and international competition juries.',
    githubUrl: 'https://github.com/ivansridana/sensamakara',
    pipeline: [
      { step: '01', name: 'Blink Input', desc: 'Eye Aspect Ratio (EAR) < 0.20 threshold via real-time camera feed' },
      { step: '02', name: 'Fuzzy Trie Engine', desc: 'N-gram candidate tree predicting next word tokens' },
      { step: '03', name: 'TTS & Game Action', desc: 'Binary trigger executes speech synthesis & game response' },
    ],
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.08, delayChildren: 0.1 } },
};

function CaseCard({ c, isOpen, onToggle }) {
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedPipelineStep, setSelectedPipelineStep] = useState(0);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.97 }}
      transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
      className={`border bg-white relative transition-all duration-300 group ${
        isOpen ? 'border-stone-900 shadow-md ring-1 ring-stone-900/5' : 'border-stone-200/90 hover:border-stone-500 hover:shadow-sm'
      }`}
    >
      {/* Top indicator rule */}
      <motion.div
        initial={false}
        animate={{ scaleX: isOpen ? 1 : 0 }}
        transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
        className="absolute top-0 left-0 right-0 h-[2px] bg-stone-900 origin-left z-20"
        aria-hidden="true"
      />

      {/* Left yellow accent line on open */}
      <div
        className={`absolute top-0 left-0 bottom-0 w-0.5 bg-yellow-500 transition-opacity duration-300 z-20 ${
          isOpen ? 'opacity-100' : 'opacity-0'
        }`}
        aria-hidden="true"
      />

      {/* Watermark index number */}
      <span
        className="absolute top-3 right-4 font-serif text-stone-100 text-4xl sm:text-5xl font-light select-none pointer-events-none transition-colors duration-300 group-hover:text-stone-200/70"
        aria-hidden="true"
      >
        {c.index}
      </span>

      {/* Card header */}
      <button
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-controls={`case-${c.id}-detail`}
        className="w-full text-left p-4 sm:p-5 md:p-6 flex flex-col justify-between relative z-10 focus:outline-none focus-visible:ring-2 focus-visible:ring-stone-900"
      >
        <div>
          <div className="flex items-center justify-between gap-2 mb-2 pr-12">
            <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-stone-500 font-medium">
              {c.label}
            </span>
          </div>

          <h3 className="text-sm sm:text-[15px] font-semibold text-stone-900 leading-snug max-w-md">
            {c.title}
          </h3>

          <p className="text-[12px] text-stone-500 mt-2.5 leading-relaxed line-clamp-2 max-w-lg">
            {c.context}
          </p>

          {/* Framework / Tech tags */}
          <div className="flex flex-wrap gap-1.5 mt-3.5 pt-1">
            {c.tags.map((tag) => (
              <span
                key={tag}
                className="text-[9px] font-mono px-2 py-0.5 bg-stone-50 text-stone-600 border border-stone-200/80 rounded-sm"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>

        {/* Toggle bar footer */}
        <div className="mt-4 pt-3 border-t border-stone-100 flex items-center justify-between w-full">
          <span className="font-mono text-[9.5px] uppercase tracking-wider text-stone-500 group-hover:text-stone-900 transition-colors">
            {isOpen ? 'Close Dossier' : 'Inspect Case Study'}
          </span>
          <span
            className={`text-xs font-mono transition-transform duration-200 ${
              isOpen ? 'text-stone-900 rotate-180' : 'text-stone-500 group-hover:text-stone-900'
            }`}
            aria-hidden="true"
          >
            {isOpen ? '▲' : '▼'}
          </span>
        </div>
      </button>

      {/* Expandable Case Dossier */}
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            key="detail"
            id={`case-${c.id}-detail`}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden border-t border-stone-200 bg-stone-50/50 relative z-10"
          >
            {/* Dossier Tabs Header */}
            <div className="flex border-b border-stone-200 px-4 sm:px-6 bg-white overflow-x-auto">
              {[
                { id: 'overview', label: '01 / Overview' },
                { id: 'approach', label: '02 / Technical Approach' },
                { id: 'deliverables', label: '03 / Deliverables' },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    setActiveTab(tab.id);
                  }}
                  className={`relative py-2.5 px-3 text-[10px] font-mono uppercase tracking-wider whitespace-nowrap transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-stone-900 ${
                    activeTab === tab.id
                      ? 'text-stone-900 font-semibold'
                      : 'text-stone-500 hover:text-stone-700'
                  }`}
                >
                  {tab.label}
                  {activeTab === tab.id && (
                    <motion.div
                      layoutId={`tab-rule-${c.id}`}
                      className="absolute bottom-0 left-0 right-0 h-[2px] bg-yellow-500"
                      transition={{ type: 'spring', stiffness: 500, damping: 35 }}
                    />
                  )}
                </button>
              ))}
            </div>

            {/* Dossier Content Body */}
            <div className="p-4 sm:p-6 space-y-4">
              {activeTab === 'overview' && (
                <motion.div
                  key="tab-overview"
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="space-y-4"
                >
                  <div>
                    <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-stone-500 mb-1.5">
                      Business &amp; Technical Context
                    </p>
                    <p className="text-[13px] text-stone-700 leading-relaxed">{c.context}</p>
                  </div>

                  <div>
                    <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-stone-500 mb-1.5">
                      Key Contribution
                    </p>
                    <p className="text-[13px] text-stone-700 leading-relaxed">{c.contribution}</p>
                  </div>
                </motion.div>
              )}

              {activeTab === 'approach' && (
                <motion.div
                  key="tab-approach"
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="space-y-4"
                >
                  <div>
                    <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-stone-500 mb-1.5">
                      Methodology &amp; Architecture
                    </p>
                    <p className="text-[13px] text-stone-700 leading-relaxed">{c.approach}</p>
                  </div>

                  {/* Interactive pipeline schematic for Sensamakara */}
                  {c.pipeline && (
                    <div className="mt-3 pt-3 border-t border-stone-200/80">
                      <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-stone-500 mb-2">
                        Interactive Input Pipeline (OpenCV → TTS)
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                        {c.pipeline.map((p, idx) => (
                          <div
                            key={p.step}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedPipelineStep(idx);
                            }}
                            className={`p-2.5 border rounded-sm cursor-pointer transition-all ${
                              selectedPipelineStep === idx
                                ? 'bg-white border-stone-900 shadow-sm ring-1 ring-stone-900/10'
                                : 'bg-stone-50/80 border-stone-200 hover:border-stone-400'
                            }`}
                          >
                            <span className="font-mono text-[9px] text-yellow-600 block mb-0.5">
                              {`${p.step} // STAGE`}
                            </span>
                            <span className="text-xs font-medium text-stone-900 block leading-tight">
                              {p.name}
                            </span>
                            <p className="text-[11px] text-stone-500 mt-1 leading-snug">
                              {p.desc}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </motion.div>
              )}

              {activeTab === 'deliverables' && (
                <motion.div
                  key="tab-deliverables"
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="space-y-4"
                >
                  <div>
                    <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-stone-500 mb-1.5">
                      Outputs &amp; Work Products
                    </p>
                    <p className="text-[13px] text-stone-700 leading-relaxed">{c.output}</p>
                  </div>

                  {c.githubUrl && (
                    <div className="pt-2">
                      <a
                        href={c.githubUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="inline-flex items-center gap-2 text-[11px] font-mono text-stone-800 hover:text-stone-900 bg-white border border-stone-300 hover:border-stone-900 px-3 py-1.5 rounded-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-stone-900 shadow-2xs"
                      >
                        <span className="font-semibold">View Repository on GitHub</span>
                        <span aria-hidden="true">↗</span>
                      </a>
                    </div>
                  )}
                </motion.div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export default function SelectedWork() {
  const [activeArea, setActiveArea] = useState('all');
  const [openCards, setOpenCards] = useState(new Set(['maturity-telecom']));

  const filtered =
    activeArea === 'all'
      ? cases
      : cases.filter((c) => c.areaRefs.includes(activeArea));

  const toggleCard = (id) => {
    setOpenCards((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  return (
    <section id="section-work" className="snap-start min-h-screen bg-stone-50/40 w-full px-4 sm:px-5 py-10 sm:py-12 md:p-20 relative">
      <div className="max-w-6xl mx-auto pt-4">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.08 }}
        >
          {/* Section telemetry header */}
          <motion.div
            variants={{
              hidden: { opacity: 0, y: 12 },
              visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
            }}
            className="flex flex-col sm:flex-row sm:items-baseline justify-between border-b border-stone-900 pb-4 mb-6 sm:mb-8 gap-2"
          >
            <div>
              <h2 className="text-xl font-serif italic text-stone-900">Selected work</h2>
            </div>
            <span className="font-mono text-[9px] uppercase tracking-widest text-stone-500">
              {filtered.length} Case Dossiers Archived
            </span>
          </motion.div>

          {/* Area filter tabs */}
          <motion.div
            variants={{
              hidden: { opacity: 0, y: 12 },
              visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
            }}
            className="flex flex-wrap gap-1.5 sm:gap-2 mb-6 md:mb-8"
            role="group"
            aria-label="Filter by area"
          >
            {areas.map((a) => (
              <button
                key={a.id}
                onClick={() => setActiveArea(a.id)}
                aria-pressed={activeArea === a.id}
                className={`relative min-h-11 md:min-h-0 text-[9px] min-[375px]:text-[10px] font-mono uppercase tracking-wider min-[375px]:tracking-widest px-3 py-1.5 border transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-stone-900 ${
                  activeArea === a.id
                    ? 'bg-stone-900 text-white border-stone-900 shadow-xs'
                    : 'bg-white text-stone-600 border-stone-200 hover:border-stone-500 hover:text-stone-900'
                }`}
              >
                {a.label}
                {activeArea === a.id && (
                  <motion.span
                    layoutId="work-filter-rule"
                    className="absolute -bottom-px left-0 right-0 h-[2px] bg-yellow-500"
                    transition={{ type: 'spring', stiffness: 480, damping: 42 }}
                    aria-hidden="true"
                  />
                )}
              </button>
            ))}
          </motion.div>

          {/* Cases Grid with fluid layout transition */}
          <motion.div layout className="grid grid-cols-1 md:grid-cols-2 gap-5 items-start">
            <AnimatePresence mode="popLayout">
              {filtered.map((c) => (
                <CaseCard
                  key={c.id}
                  c={c}
                  isOpen={openCards.has(c.id)}
                  onToggle={() => toggleCard(c.id)}
                />
              ))}
            </AnimatePresence>
          </motion.div>

          {filtered.length === 0 && (
            <p className="text-sm text-stone-500 py-8 font-mono">No cases in this area yet.</p>
          )}
        </motion.div>
      </div>
    </section>
  );
}

